/**
 * Training timer cues — parity with iOS SoundManager.
 * Assets: start_sequence_sound.wav, end_sequence_sound.wav, end_exercise_sound.wav
 *
 * Sequence rule (same as iOS):
 *   remainingSeconds > 0 → start sequence
 *   remainingSeconds === 0 → end sequence
 */

import { publicAssetUrl } from "../config";
import {
  TE_STATE_CANCELLED,
  TE_STATE_FINALIZED,
  TE_STATE_STARTED,
} from "./coachExerciseNavigation";
import { unlockEvalPreCaptureCountdownAudio } from "./evalPreCaptureCountdown";

const SEQUENCE_DEDUPE_MS = 2500;
const EXERCISE_DEDUPE_MS = 10000;

/** @type {Record<string, HTMLAudioElement | null>} */
const players = {
  startSequence: null,
  endSequence: null,
  endExercise: null,
};

let lastSequenceAlarmKey = null;
let lastSequenceAlarmAt = 0;
let lastExerciseEndAlarmKey = null;
let lastExerciseEndAlarmAt = 0;
let lastDedupeWaveKey = null;
let cueAudioUnlocked = false;

function waveMediaKey(mediaSyncAt, synchronizedStartTime, teId) {
  const sync = Number(mediaSyncAt);
  if (Number.isFinite(sync) && sync > 0) return String(sync);
  const start = Number(synchronizedStartTime);
  if (Number.isFinite(start) && start > 0) return String(start);
  return teId != null && teId !== "" ? String(teId) : "";
}

function prepareAudioEl(audio) {
  if (!audio) return;
  audio.preload = "auto";
  audio.volume = 1;
  audio.playsInline = true;
}

function ensurePlayers() {
  if (typeof window === "undefined" || typeof Audio === "undefined") return;
  if (!players.startSequence) {
    players.startSequence = new Audio(
      publicAssetUrl("/start_sequence_sound.wav")
    );
    prepareAudioEl(players.startSequence);
  }
  if (!players.endSequence) {
    players.endSequence = new Audio(publicAssetUrl("/end_sequence_sound.wav"));
    prepareAudioEl(players.endSequence);
  }
  if (!players.endExercise) {
    players.endExercise = new Audio(publicAssetUrl("/end_exercise_sound.wav"));
    prepareAudioEl(players.endExercise);
  }
}

function playAudio(audio, label) {
  if (!audio) return false;
  try {
    audio.pause();
    audio.muted = false;
    audio.volume = 1;
    audio.currentTime = 0;
    const p = audio.play();
    if (p && typeof p.catch === "function") {
      p.catch((err) =>
        console.warn(`SoundManager: ${label} play blocked/failed:`, err)
      );
    }
    return true;
  } catch (err) {
    console.warn(`SoundManager: ${label} error:`, err);
    return false;
  }
}

function silentUnlock(audio) {
  if (!audio) return Promise.resolve(false);
  const wasMuted = audio.muted;
  audio.muted = true;
  try {
    const p = audio.play();
    if (p && typeof p.then === "function") {
      return p
        .then(() => {
          audio.pause();
          audio.currentTime = 0;
          audio.muted = wasMuted;
          return true;
        })
        .catch(() => {
          audio.muted = wasMuted;
          return false;
        });
    }
    audio.pause();
    audio.currentTime = 0;
    audio.muted = wasMuted;
    return Promise.resolve(true);
  } catch {
    audio.muted = wasMuted;
    return Promise.resolve(false);
  }
}

/**
 * Mobile Safari / Chrome block HTMLAudio until a user gesture.
 * Call from pointerdown/touchstart on the Live Training player.
 */
export function unlockTrainingCueAudio() {
  if (typeof window === "undefined") return Promise.resolve(false);
  ensurePlayers();
  const jobs = [
    silentUnlock(players.startSequence),
    silentUnlock(players.endSequence),
    silentUnlock(players.endExercise),
    Promise.resolve(unlockEvalPreCaptureCountdownAudio()),
  ];
  return Promise.all(jobs).then((results) => {
    const ok = results.some(Boolean);
    if (ok) cueAudioUnlocked = true;
    return ok;
  });
}

export function isTrainingCueAudioUnlocked() {
  return cueAudioUnlocked;
}

export function preloadTrainingCueAudio() {
  ensurePlayers();
  try {
    players.startSequence?.load?.();
    players.endSequence?.load?.();
    players.endExercise?.load?.();
  } catch {
    /* ignore */
  }
}

/** iOS SoundManager.resetTimerAlarmDedupeForNewExercise */
export function resetTimerAlarmDedupeForNewExercise(_teId = null, waveKey = null) {
  const key =
    waveKey != null && String(waveKey) !== "" ? String(waveKey) : null;
  if (key && key === lastDedupeWaveKey) return false;
  if (key) lastDedupeWaveKey = key;
  lastSequenceAlarmKey = null;
  lastSequenceAlarmAt = 0;
  lastExerciseEndAlarmKey = null;
  lastExerciseEndAlarmAt = 0;
  return true;
}

/** Sequence block starting (remaining > 0). */
export function playStartSequenceSound() {
  ensurePlayers();
  return playAudio(players.startSequence, "start_sequence_sound.wav");
}

/** Sequence block ending (remaining === 0). */
export function playEndSequenceSound() {
  ensurePlayers();
  return playAudio(players.endSequence, "end_sequence_sound.wav");
}

/**
 * iOS-parity sequence alarm from a server sequenceUpdate payload.
 * Dedupe by shared wave clock so sibling stations do not play N times.
 * @param {{ teId?: string|null, sequenceOrder?: number|null, remainingSeconds?: number|null, mediaSyncAt?: number|null, synchronizedStartTime?: number|null }} opts
 */
export function playSequenceAlarmFromServer({
  teId = null,
  sequenceOrder = 0,
  remainingSeconds = 0,
  mediaSyncAt = null,
  synchronizedStartTime = null,
} = {}) {
  const remaining = Number(remainingSeconds) || 0;
  const kind = remaining > 0 ? "start" : "end";
  const wave = waveMediaKey(mediaSyncAt, synchronizedStartTime, teId);
  const key = `${wave}-${sequenceOrder ?? 0}-${kind}`;
  const now = Date.now();
  if (
    key === lastSequenceAlarmKey &&
    now - lastSequenceAlarmAt < SEQUENCE_DEDUPE_MS
  ) {
    return false;
  }
  lastSequenceAlarmKey = key;
  lastSequenceAlarmAt = now;
  return remaining > 0 ? playStartSequenceSound() : playEndSequenceSound();
}

/**
 * Full exercise finished.
 * @param {string|null} [teId]
 * @param {number|string|null} [revision] exercise_timer_revision from server (dedupe replays)
 * @param {number|string|null} [mediaSyncAt] shared wave clock (preferred over teId)
 */
export function playEndExerciseSound(teId = null, revision = null, mediaSyncAt = null) {
  const wave = waveMediaKey(mediaSyncAt, null, teId);
  const key = wave
    ? `exercise-end-${wave}`
    : `exercise-end-${teId ?? ""}-${revision ?? ""}`;
  const now = Date.now();
  if (
    key === lastExerciseEndAlarmKey &&
    now - lastExerciseEndAlarmAt < EXERCISE_DEDUPE_MS
  ) {
    return false;
  }
  lastExerciseEndAlarmKey = key;
  lastExerciseEndAlarmAt = now;
  ensurePlayers();
  return playAudio(players.endExercise, "end_exercise_sound.wav");
}

/** @deprecated Prefer playEndExerciseSound — kept for existing imports. */
export function playExerciseFinalizationSound(teId = null) {
  return playEndExerciseSound(teId);
}

/**
 * @deprecated Prefer playSequenceAlarmFromServer — maps to start/end by remaining.
 * If called with no args, plays end-sequence (legacy web behavior).
 */
export function playSequenceAlarmSound(remainingSeconds) {
  if (remainingSeconds == null) {
    return playEndSequenceSound();
  }
  return playSequenceAlarmFromServer({
    remainingSeconds: Number(remainingSeconds) || 0,
  });
}

/** Extract remaining sequence seconds from a socket payload. */
export function sequenceRemainingFromPayload(data) {
  if (!data || typeof data !== "object") return 0;
  const raw =
    data.sequenceTimer ?? data.remaining_sequence_time_seconds ?? 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function payloadStateId(data) {
  return data?.te_state || data?.tbl_state_te?.st_id || null;
}

/** sequenceUpdate with alarmType sequence — start (remaining > 0) or end (0). */
export function shouldPlaySequenceAlarmFromPayload(data) {
  return Boolean(data?.alarm && data.alarmType === "sequence");
}

/**
 * iOS player plays the exercise-end cue on finalized/cancelled when the server
 * marks the payload as an alarm (or legacy payloads without an alarm field).
 */
export function shouldPlayExerciseEndFromPayload(data) {
  if (!data) return false;
  if (data.finalize_idempotent === true) return false;
  const stateId = payloadStateId(data);
  const isEndState =
    stateId === TE_STATE_FINALIZED ||
    stateId === "finalized" ||
    stateId === TE_STATE_CANCELLED ||
    stateId === "cancelled";
  if (!isEndState) return false;
  if (data.alarm === true) return true;
  if (data.alarm === false) return false;
  return true;
}

export function shouldResetTimerAlarmDedupeFromPayload(data) {
  if (!data) return false;
  const stateId = payloadStateId(data);
  return stateId === TE_STATE_STARTED || stateId === "started";
}

export function playSequenceAlarmFromPayload(data) {
  if (!shouldPlaySequenceAlarmFromPayload(data)) return false;
  return playSequenceAlarmFromServer({
    teId: data.te_id,
    sequenceOrder: data.currentSequence ?? 0,
    remainingSeconds: sequenceRemainingFromPayload(data),
    mediaSyncAt: data.media_sync_at ?? data.mediaSyncAt ?? null,
    synchronizedStartTime: data.synchronizedStartTime ?? null,
  });
}

export function playExerciseEndFromPayload(data) {
  if (!shouldPlayExerciseEndFromPayload(data)) return false;
  return playEndExerciseSound(
    data.te_id,
    data.exercise_timer_revision ?? null,
    data.media_sync_at ?? data.mediaSyncAt ?? data.synchronizedStartTime ?? null
  );
}

/** Wave-clock remaining seconds (sequence or exercise). */
export function remainingSecFromWallClock(startMs, durationSec, nowMs = Date.now()) {
  const start = Number(startMs);
  const duration = Number(durationSec);
  if (!(start > 0) || !Number.isFinite(duration) || duration <= 0) return null;
  return Math.max(0, duration - Math.floor((nowMs - start) / 1000));
}

/**
 * iOS local countdown boundary: remaining crossed from >0 to 0.
 * @param {number|null|undefined} prevRemaining
 * @param {number|null|undefined} remaining
 */
export function didLocalTimerHitZero(prevRemaining, remaining) {
  const prev = Number(prevRemaining);
  const next = Number(remaining);
  if (!Number.isFinite(prev) || !Number.isFinite(next)) return false;
  return prev > 0 && next === 0;
}
